
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CoreService } from 'src/app/services/core.service';
import { GeoCodingService } from 'src/app/services/geo-coding.service';
import { LoaderService } from 'src/app/services/loader.service';
import { UsersService } from 'src/app/services/users.service';
import { AddressMapReference } from '../dialog-address/dialog-address.component';

interface DialogNewAddressData {
  referenceLocation?: AddressMapReference;
  address?: any;
  complementAddress?: boolean;
}

@Component({
  selector: 'app-dialog-new-address',
  templateUrl: './dialog-new-address.component.html',
  styleUrls: ['./dialog-new-address.component.scss']
})
export class DialogNewAddressComponent implements OnInit {
  readonly defaultLatitude = 25.537911020290082;
  readonly defaultLongitude = -103.41543316841125;

  addressForm: FormGroup;
  localities: any[] = [];

  mapLatitude = this.defaultLatitude;
  mapLongitude = this.defaultLongitude;
  latitude = 0;
  longitude = 0;
  zoom = 13;

  addressFromMap = '';
  isReferenceLocation = false;
  pendingLocalityName = '';
  isSaving = false;

  referenceAddress = '';
  referenceLatitude = 0;
  referenceLongitude = 0;
  hasReferenceLocation = false;
  hasSelectedLocation = false;
  selectedMapAddress = '';

  prevData: any = null;
  complementAddress = false;

  constructor(
    private formBuilder: FormBuilder,
    private loaderService: LoaderService,
    private coreService: CoreService,
    private usersService: UsersService,
    private geocodingService: GeoCodingService,
    private dialogRef: MatDialogRef<DialogNewAddressComponent>,
    @Inject(MAT_DIALOG_DATA) private data: DialogNewAddressData | null
  ) {
    this.complementAddress = data?.complementAddress === true;
    this.prevData = this.complementAddress ? data?.address ?? null : null;

    this.addressForm = this.formBuilder.group({
      receiving_name: ['', [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(64)
      ]],
      state_id: ['', Validators.required],
      municipality_id: ['', Validators.required],
      locality_id: ['', Validators.required],
      zip_code: ['', [
        Validators.required,
        Validators.minLength(5),
        Validators.maxLength(5)
      ]],
      phone: ['', [
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(10),
        Validators.pattern(/^\d+$/)
      ]],
      street: ['', [
        Validators.required,
        Validators.minLength(4),
        Validators.maxLength(128)
      ]],
      references: ['', [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(128)
      ]],
      ext_number: ['', [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(10)
      ]],
      lat: ['', Validators.required],
      lng: ['', Validators.required],
      state: [''],
      municipality: ['']
    });
  }

  ngOnInit(): void {
    if (this.prevData) {
      // console.log(this.prevData)
      this.fillFormFromSavedAddress(this.prevData);
    }

    const referenceLocation: any = this.data?.referenceLocation;

    if (referenceLocation) {
      this.fillFormFromReference(referenceLocation);
    }

    // En modo complementación no modificamos la ubicación guardada.
    if (this.complementAddress && this.prevData) {
      this.latitude = Number(this.prevData.lat);
      this.longitude = Number(this.prevData.lng);

      this.addressForm.patchValue({
        lat: this.prevData.lat,
        lng: this.prevData.lng
      }, { emitEvent: false });
    }
  }

  private fillFormFromSavedAddress(address: any): void {
    let phone = String(address.phone ?? '').replace(/\D/g, '');

    if (phone.startsWith('52') && phone.length === 12) {
      phone = phone.substring(2);
    }

    const savedLatitude = Number(address.lat);
    const savedLongitude = Number(address.lng);

    this.pendingLocalityName =
      address.locality?.name ??
      address.locality_name ??
      (typeof address.locality === 'string' ? address.locality : '');

    this.addressForm.patchValue({
      receiving_name: address.receiving_name ?? '',
      phone,
      street: address.street ?? '',
      ext_number: address.ext_number ?? '',
      references: address.references ?? '',
      zip_code: address.zip_code ?? '',
      locality_id: address.locality?.id ?? address.locality_id ?? '',
      municipality_id: address.municipality?.id ?? address.municipality_id ?? '',
      state_id: address.state?.id ?? address.state_id ?? '',
      municipality: address.municipality?.name ?? address.municipality ?? '',
      state: address.state?.name ?? address.state ?? '',
      lat: address.lat ?? '',
      lng: address.lng ?? ''
    }, { emitEvent: false });

    if (
      Number.isFinite(savedLatitude) &&
      Number.isFinite(savedLongitude) &&
      !(savedLatitude === 0 && savedLongitude === 0)
    ) {
      this.latitude = savedLatitude;
      this.longitude = savedLongitude;
      this.mapLatitude = savedLatitude;
      this.mapLongitude = savedLongitude;
    }

    const zipCode = String(address.zip_code ?? '');

    if (zipCode.length === 5) {
      this.searchDataZipCode();
    }
  }

  private fillFormFromReference(referenceLocation: any): void {
    const latitude = Number(referenceLocation?.geometry?.location?.lat);
    const longitude = Number(referenceLocation?.geometry?.location?.lng);

    this.referenceAddress =
      referenceLocation?.formatted_address ??
      referenceLocation?.address ??
      '';

    if (
      Number.isFinite(latitude) &&
      Number.isFinite(longitude) &&
      !(latitude === 0 && longitude === 0)
    ) {
      this.referenceLatitude = latitude;
      this.referenceLongitude = longitude;
      this.hasReferenceLocation = true;

      if (!this.complementAddress) {
        this.latitude = latitude;
        this.longitude = longitude;
        this.mapLatitude = latitude;
        this.mapLongitude = longitude;

        this.addressForm.patchValue({
          lat: latitude,
          lng: longitude
        });
      }
    }

    const components = this.getAddressComponents(
      referenceLocation?.address_components ?? []
    );

    const current = this.addressForm.getRawValue();
    const patch: any = {};

    // Para direcciones existentes solo completamos campos vacíos.
    if (!current.street && components['route']) {
      patch.street = components['route'];
    }

    if (!current.ext_number && components['street_number']) {
      patch.ext_number = components['street_number'];
    }

    if (!current.zip_code && components['postal_code']) {
      patch.zip_code = components['postal_code'];
    }

    this.addressForm.patchValue(patch);

    if (!this.pendingLocalityName) {
      this.pendingLocalityName =
        components['neighborhood'] ??
        components['sublocality'] ??
        components['sublocality_level_1'] ??
        components['locality'] ??
        '';
    }

    const zipCode = String(this.zipCode?.value ?? '');

    if (zipCode.length === 5 && this.localities.length === 0) {
      this.searchDataZipCode();
    }
  }

  get receivingName() {
    return this.addressForm.get('receiving_name');
  }

  get street() {
    return this.addressForm.get('street');
  }

  get extNumber() {
    return this.addressForm.get('ext_number');
  }

  get zipCode() {
    return this.addressForm.get('zip_code');
  }

  get localityId() {
    return this.addressForm.get('locality_id');
  }

  get phone() {
    return this.addressForm.get('phone');
  }

  get references() {
    return this.addressForm.get('references');
  }

  get lat() {
    return this.addressForm.get('lat');
  }

  get lng() {
    return this.addressForm.get('lng');
  }

  get hasLocation(): boolean {
    return this.latitude !== 0 && this.longitude !== 0;
  }

  getErrorMessage(controlName: string): string {
    const errors = this.addressForm.get(controlName)?.errors;

    if (errors?.['required']) {
      return 'Este campo es requerido';
    }

    if (errors?.['minlength']) {
      return `La longitud mínima es ${errors['minlength'].requiredLength} caracteres`;
    }

    if (errors?.['maxlength']) {
      return `La longitud máxima es ${errors['maxlength'].requiredLength} caracteres`;
    }

    if (errors?.['pattern']) {
      return 'Formato inválido';
    }

    return '';
  }

  searchDataZipCode(): void {
    const zipCode = String(this.zipCode?.value ?? '').trim();

    if (zipCode.length !== 5) {
      this.localities = [];

      this.addressForm.patchValue({
        locality_id: '',
        municipality_id: '',
        state_id: '',
        municipality: '',
        state: ''
      });

      return;
    }

    this.loaderService.show();

    this.usersService.getLocalitiesByZipCode(zipCode).subscribe({
      next: (response) => {
        if (response.status) {
          this.localities = response.data ?? [];
          this.selectMatchedLocality();
        } else {
          this.localities = [];
          this.showError(
            'No fue posible obtener las colonias de este código postal.'
          );
        }

        this.loaderService.hide();
      },
      error: (error) => {
        console.error(error);
        this.localities = [];
        this.showError(
          'No fue posible obtener las colonias de este código postal.'
        );
        this.loaderService.hide();
      }
    });
  }

  onSelectionLocality(localityId: number | string): void {
    const selectedId = Number(localityId);

    const locality = this.localities.find(
      item => Number(item.id) === selectedId
    );

    if (!locality) {
      return;
    }

    this.addressForm.patchValue({
      locality_id: locality.id,
      municipality_id: locality.municipality?.id ?? '',
      state_id: locality.state?.id ?? '',
      municipality: locality.municipality?.name ?? '',
      state: locality.state?.name ?? ''
    });
  }

  onMapClickEvent(event: any): void {
    if (this.complementAddress) {
      return;
    }

    this.setCoordinates(event.coords.lat, event.coords.lng);
  }

  markerDragEnd(event: any): void {
    if (this.complementAddress) {
      return;
    }

    this.setCoordinates(event.coords.lat, event.coords.lng);
  }

  saveAddress(): void {
    if (this.isSaving) {
      return;
    }

    if (this.addressForm.invalid) {
      this.addressForm.markAllAsTouched();
      this.showError(
        this.complementAddress
          ? 'Completa los datos requeridos de la dirección.'
          : 'Completa los datos requeridos y marca tu ubicación en el mapa.'
      );
      return;
    }

    const formValue = this.addressForm.getRawValue();
    const phone = String(formValue.phone ?? '').replace(/\D/g, '');

    const addressData: any = {
      ...formValue,
      phone: phone.startsWith('52') && phone.length === 12
        ? phone
        : `52${phone}`,
      lat: String(
        this.complementAddress && this.prevData
          ? this.prevData.lat
          : formValue.lat
      ),
      lng: String(
        this.complementAddress && this.prevData
          ? this.prevData.lng
          : formValue.lng
      )
    };

    this.isSaving = true;
    this.loaderService.show();

    const request = this.complementAddress && this.prevData
      ? this.usersService.updateAddress(addressData, this.prevData.id)
      : this.usersService.createAddress(addressData);

    request.subscribe({
      next: (response: any) => {
        if (response.status) {
          this.coreService.emitSubjet.next({
            type: 'Snackbar',
            payload: {
              text: response.message ||
                (this.complementAddress
                  ? 'Dirección actualizada correctamente.'
                  : 'Dirección guardada correctamente.')
            }
          });

          const savedAddress = {
            ...(this.prevData ?? {}),
            ...addressData,
            ...(response.data ?? {}),
            lat: addressData.lat,
            lng: addressData.lng
          };

          this.dialogRef.close({
            created: true,
            updated: this.complementAddress,
            address: savedAddress,
            addressData
          });
        } else {
          this.showError(
            response.message || 'No fue posible guardar la dirección.'
          );
        }

        this.finishSaving();
      },
      error: (error: any) => {
        console.error(error);
        this.showError(
          'No fue posible guardar la dirección. Inténtalo nuevamente.'
        );
        this.finishSaving();
      }
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  private setCoordinates(latitude: number, longitude: number): void {
    this.latitude = latitude;
    this.longitude = longitude;
    this.isReferenceLocation = false;

    this.addressForm.patchValue({
      lat: latitude,
      lng: longitude
    });

    this.getAddressFromCoordinates(latitude, longitude);
  }

  private getAddressFromCoordinates(
    latitude: number,
    longitude: number
  ): void {
    this.geocodingService.getLocation(latitude, longitude).subscribe({
      next: (response) => {
        const result = response?.results?.[0];

        if (!result) {
          this.selectedMapAddress = '';
          return;
        }

        this.selectedMapAddress = result.formatted_address ?? '';

        const components = this.getAddressComponents(
          result.address_components ?? []
        );

        const patch: any = {};

        if (!this.street?.value && components['route']) {
          patch.street = components['route'];
        }

        if (!this.extNumber?.value && components['street_number']) {
          patch.ext_number = components['street_number'];
        }

        if (!this.zipCode?.value && components['postal_code']) {
          patch.zip_code = components['postal_code'];
        }

        if (Object.keys(patch).length > 0) {
          this.addressForm.patchValue(patch);
        }

        const locality =
          components['neighborhood'] ??
          components['sublocality'] ??
          components['sublocality_level_1'] ??
          components['locality'];

        if (locality) {
          this.pendingLocalityName = locality;
        }

        if (
          patch.zip_code ||
          (this.zipCode?.value && this.localities.length === 0)
        ) {
          this.searchDataZipCode();
        }
      },
      error: (error) => {
        console.error(error);
        this.selectedMapAddress = '';
      }
    });
  }

  private getAddressComponents(
    addressComponents: any[]
  ): Record<string, string> {
    return addressComponents.reduce(
      (components: Record<string, string>, component: any) => {
        component.types?.forEach((type: string) => {
          components[type] = component.long_name;
        });

        return components;
      },
      {}
    );
  }

  
  private selectMatchedLocality(): void {
    if (!this.localities?.length) {
      return;
    }

    const normalize = (value: any) =>
      String(value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLocaleLowerCase();

    const localityId = this.addressForm.get('locality_id')?.value;

    let locality = this.localities.find(
      (item: any) => String(item.id) === String(localityId)
    );

  
    if (!locality && this.pendingLocalityName) {
      const pendingName = normalize(this.pendingLocalityName);

      locality = this.localities.find(
        (item: any) => normalize(item.name) === pendingName
      );
    }

    if (locality) {
      this.onSelectionLocality(locality.id);
    }
  }


  private finishSaving(): void {
    this.isSaving = false;
    this.loaderService.hide();
  }

  private showError(text: string): void {
    this.coreService.emitSubjet.next({
      type: 'Snackbar',
      payload: { text }
    });
  }
}
